/*
    Calculates the late fee applied based on a company's fee settings
*/

import { clamp, isEmpty, omit, round, subtract } from 'lodash';

import {
  AppPaymentDueFragment,
  CompanyLateFeeSettingsFixed as GqlCompanyLateFeeSettingsFixed,
  CompanyLateFeeSettingsPercent as GqlCompanyLateFeeSettingsPercent,
  CompanyLateFeeType as GqlCompanyLateFeeType,
  LoanLateFee as GqlLoanLateFee,
  LoanLateFeeType as GqlLoanLateFeeType,
} from '@willow/graphql-iso/src/app';
import {
  CompanyLateFeeSettings,
  CompanyLateFeeSettingsFixed,
  CompanyLateFeeSettingsPercent,
  LoanLateFee,
  LoanLateFeeFixed,
  LoanLateFeePercent,
  PaymentDue,
} from '@willow/types-iso';

import { multiply } from '../utils/math';
import { percentageToDecimal } from '../utils/percentageToDecimal';

const calculatePercentFee = ({
  amountToApplyFee,
  decimalCharge,
  minAmount,
  maxAmount,
}: {
  amountToApplyFee: number;
  decimalCharge: number;
  minAmount: number | undefined;
  maxAmount: number | undefined;
}): number => {
  const baseOverrideAmount = multiply(decimalCharge, amountToApplyFee);
  const boundedOverrideAmount = clamp(baseOverrideAmount, minAmount ?? -Infinity, maxAmount ?? Infinity);
  return round(boundedOverrideAmount, 2);
};

export function getLateFeeAmount(
  companyFeeSettings: CompanyLateFeeSettings,
  paymentDue: PaymentDue,
  loanOverrides: LoanLateFee | undefined,
  loanLateFeeStopSettingEnabled: boolean,
): number;
export function getLateFeeAmount(
  companyFeeSettings: GqlCompanyLateFeeSettingsFixed | GqlCompanyLateFeeSettingsPercent,
  paymentDue: AppPaymentDueFragment,
  loanOverrides: GqlLoanLateFee | undefined,
  loanLateFeeStopSettingEnabled: boolean,
): number;
export function getLateFeeAmount(
  companyFeeSettings: CompanyLateFeeSettings | GqlCompanyLateFeeSettingsFixed | GqlCompanyLateFeeSettingsPercent,
  paymentDue: PaymentDue | AppPaymentDueFragment,
  loanOverrides: LoanLateFee | GqlLoanLateFee | undefined,
  loanLateFeeStopSettingEnabled: boolean,
): number {
  if (loanLateFeeStopSettingEnabled) return 0;

  const terms = getLateFeeTerms(companyFeeSettings, loanOverrides);
  if (!terms) return 0;

  if (terms.type === 'fixed') {
    return terms.amount;
  }

  /*
    Determine the amount to apply the fee from
    * The fee is calculated on the statement P&I amount
    * We use the payment due total so that draws are included in the fee calculation
    * Partial payments of this payment due are not considered
  */
  const amountToApplyFee = subtract(paymentDue.total, paymentDue.escrow);
  return calculatePercentFee({ amountToApplyFee, ...terms });
}

export type LateFeeTerms =
  | { type: 'percent'; decimalCharge: number; minAmount: number | undefined; maxAmount: number | undefined }
  | { type: 'fixed'; amount: number };

/**
 * An override replaces the company terms outright, so an override with no min or max is unbounded
 * rather than inheriting the company's bounds. `undefined` means no late fee applies.
 */
export function getLateFeeTerms(
  companyFeeSettings: CompanyLateFeeSettings | GqlCompanyLateFeeSettingsFixed | GqlCompanyLateFeeSettingsPercent,
  loanOverrides: LoanLateFee | GqlLoanLateFee | undefined,
): LateFeeTerms | undefined {
  const { enabled, appliedToAllLoans } = companyFeeSettings;
  if (!enabled) return undefined;

  const hasLoanOverride = loanOverrides && !isEmpty(omit(loanOverrides, '__typename'));
  if (!appliedToAllLoans && !hasLoanOverride) return undefined;

  if (hasLoanOverride) {
    switch (loanOverrides.type) {
      case 'percent':
      case GqlLoanLateFeeType.Percent: {
        const percentOverride = loanOverrides as LoanLateFeePercent;
        return {
          type: 'percent',
          decimalCharge: percentOverride.decimalCharge,
          minAmount: percentOverride.minAmount,
          maxAmount: percentOverride.maxAmount,
        };
      }
      case 'fixed':
      case GqlLoanLateFeeType.Fixed: {
        const fixedOverride = loanOverrides as LoanLateFeeFixed;
        return { type: 'fixed', amount: fixedOverride.amount };
      }
      default:
        return undefined;
    }
  }

  switch (companyFeeSettings.type) {
    case 'percent':
    case GqlCompanyLateFeeType.Percent: {
      const percentSettings = companyFeeSettings as CompanyLateFeeSettingsPercent;
      return {
        type: 'percent',
        decimalCharge: percentageToDecimal(percentSettings.percentCharge),
        minAmount: percentSettings.minAmount,
        maxAmount: percentSettings.maxAmount,
      };
    }
    case 'fixed':
    case GqlCompanyLateFeeType.Fixed: {
      const fixedSettings = companyFeeSettings as CompanyLateFeeSettingsFixed;
      return { type: 'fixed', amount: fixedSettings.amount };
    }
    default:
      return undefined;
  }
}
